// src/components/Navbar.styles.ts
//
// Navbar styling.
//
// Three things constrain this file, and all three are contracts with code
// outside it:
//
// 1. HEIGHT. HeroSection.styles.ts sets `min-height: calc(100vh - 62px)` so the
//    hero fits the first viewport underneath a sticky navbar. That 62px is
//    therefore load-bearing, not decorative, so `NAVBAR_HEIGHT` below is
//    asserted as a min-height and the Query CTA is sized to fit inside it.
//    Changing this constant means changing the hero.
//
// 2. GLOBAL RULES in globalStyles.ts that outrank a plain class selector:
//      - `a[href]:hover { text-decoration: underline }`  -> reset on nav links
//      - `button:hover { transform; box-shadow }`          -> reset on MenuButton
//      - `a[href]:focus-visible { outline: none; box-shadow }` at (0,2,1)
//        outranks `.class:focus-visible` at (0,2,0), so a locally declared
//        `outline` is silently dropped. The focus rules below use `&&` to reach
//        (0,3,0) and win, then restate a theme-aware ring.
//
// 3. THE MOBILE/DESKTOP SPLIT. WhatIDoSection and ExperienceHighlights already
//    branch in JS with `useMediaQuery` rather than rendering both and hiding one
//    with CSS, so screen readers never see duplicate navigation. The navbar
//    follows the same rule.
//
// Deliberately not used: a blurred backdrop, a gradient surface, or a box-shadow
// that changes on scroll. The surface is a near-opaque tint of the page
// background with a single hairline border.
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import { minDevice } from '../styles/breakpoints';
import Button from './Button';

const monoStack =
  "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

/** See contract 1 above. */
export const NAVBAR_HEIGHT = '62px';

/** The viewport at which the desktop row is replaced by the menu button. */
export const DESKTOP_QUERY = minDevice.laptop;

/**
 * `theme.background` at a reduced alpha. The navbar sits over scrolling
 * content, so a fully opaque bar would cut a hard seam across the page's
 * background gradient; a near-opaque tint keeps the seam invisible without
 * resorting to a blur, which reads as glassmorphism.
 *
 * The page background is an opaque hex, so the channels have to be unpacked.
 * An unexpected value falls back to the opaque token rather than throwing.
 *
 * TODO: this belongs in a shared theme util. ProjectCallToAction.styles.ts
 * already has a near-identical luminance/hex helper for the same reason.
 */
const withAlpha = (hex: string, alpha: number): string => {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());

  if (!match) return hex;

  const packed = parseInt(match[1], 16);
  const r = (packed >> 16) & 255;
  const g = (packed >> 8) & 255;
  const b = packed & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * The focus ring. `&&` is load-bearing: see contract 2. The inner ring is drawn
 * in the navbar surface colour so it stays visible against the page content
 * showing through, and the outer ring uses the theme accent for contrast in
 * both light and dark.
 */
const focusRing = css`
  &&:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => withAlpha(theme.background, 0.92)},
      0 0 0 4px ${({ theme }) => theme.accent};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
`;

/** Guard for rules that outrank the global reduced-motion block. */
const noMotion = css`
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * As `noMotion`, for elements that also transition a pseudo-element. The active
 * route indicator is an `::after`, and leaving its scale transition live would
 * be the one moving thing left on the page.
 */
const noMotionWithAfter = css`
  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::after {
      transition: none;
    }
  }
`;

/* ----------------------------------------------------------------- shell */

export const NavRoot = styled.nav`
  position: sticky;
  top: 0;
  /* Above the fixed noise overlay in globalStyles.ts (z-index 0) and below
     nothing else on the page. */
  z-index: 100;
  width: 100%;
  min-height: ${NAVBAR_HEIGHT};
  background: ${({ theme }) => withAlpha(theme.background, 0.92)};
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  color: ${({ theme }) => theme.text};
  box-sizing: border-box;
`;

export const NavInner = styled.div`
  /* Same gutter and measure as every section on the page, so the brand and the
     nav align with the content that scrolls underneath. */
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
  min-height: ${NAVBAR_HEIGHT};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
`;

/* ----------------------------------------------------------------- brand */

export const Brand = styled(Link)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.15rem;
  min-width: 0;
  text-decoration: none;

  /* globalStyles.ts underlines every a[href] on hover. */
  &:hover {
    text-decoration: none;
  }

  ${focusRing}
`;

export const BrandName = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.headingColor};
  white-space: nowrap;
  transition: color 0.2s ease;

  ${Brand}:hover & {
    color: ${({ theme }) => theme.accent};
  }

  ${noMotion}
`;

export const BrandRole = styled.span`
  font-family: ${monoStack};
  font-size: 0.65rem;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  white-space: nowrap;
`;

/* ------------------------------------------------------- desktop nav list */

export const NavList = styled.ul`
  display: flex;
  align-items: center;
  gap: 1.85rem;
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const NavItem = styled.li`
  list-style: none;
`;

export interface NavLinkProps {
  $active: boolean;
}

export const NavLink = styled(Link)<NavLinkProps>`
  position: relative;
  display: inline-block;
  padding-bottom: 5px;
  font-size: 0.925rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  line-height: 1.2;
  white-space: nowrap;
  text-decoration: none;
  color: ${({ $active, theme }) => ($active ? theme.headingColor : theme.colors.secondary)};
  /* Colour and the indicator only. The old navbar translated the item on
     hover, which is the kind of movement that reads as a template. */
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.headingColor};
    text-decoration: none;
  }

  /* The active indicator. scaleX rather than width so the transition runs on
     the compositor and never triggers layout. */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 2px;
    background: ${({ theme }) => theme.accent};
    transform: scaleX(${({ $active }) => ($active ? 1 : 0)});
    transform-origin: left center;
    transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &:hover::after {
    transform: scaleX(1);
  }

  ${focusRing}
  ${noMotionWithAfter}
`;

/* ------------------------------------------------------ dialog-open action */

/**
 * A nav entry that opens a window instead of navigating.
 *
 * It is a <button>, not a <Link>, for the same reason QueryCta is: opening a
 * dialog is an action. There is no href to middle-click and the URL does not
 * change, so a link would be a control wearing the wrong costume. It is styled
 * to match NavLink rather than QueryCta, because it holds a place in the
 * navigation list instead of sitting apart from it as a call to action.
 *
 * The two concerns that forced `&&` elsewhere apply here too: globalStyles.ts
 * lifts and shadows every button on hover, and the a[href] rules do not exist
 * for a button at all, so the text-decoration resets NavLink needs are dropped
 * and only the motion reset is required.
 */
export const NavAction = styled.button`
  position: relative;
  display: inline-block;
  padding: 0 0 5px;
  font-family: inherit;
  font-size: 0.925rem;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  text-align: left;
  color: ${({ theme }) => theme.colors.secondary};
  background: none;
  border: 0;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.headingColor};
    /* Beats the global button:hover rule, which would make the navbar jump: a
       class selector plus :hover is (0,2,0) against (0,1,1). */
    transform: none;
    box-shadow: none;
  }

  /* The same underline indicator as NavLink, resting at scaleX(0). There is no
     $active state to honour: this entry is never a current route. */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 2px;
    background: ${({ theme }) => theme.accent};
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &:hover::after {
    transform: scaleX(1);
  }

  ${focusRing}
  ${noMotionWithAfter}
`;

/* ------------------------------------------------------ right-hand controls */

/**
 * The controls that sit at the trailing end of the bar.
 *
 * NavInner is a space-between flex row, so its children are the three groups it
 * spaces out: brand, navigation list, and these. Before this existed the Query
 * CTA was a direct child, and adding a second trailing control would have
 * turned space-between into "spread everything evenly" and torn the CTA away
 * from the toggle beside it. Grouping them keeps the bar's three-part rhythm
 * regardless of how many controls land here.
 */
export const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  /* The brand has min-width: 0 and the list can wrap its own labels; without
     this a long nav list could squeeze the controls. */
  flex-shrink: 0;
`;

/* -------------------------------------------------------------- Query CTA */

/**
 * The Query CTA.
 *
 * Wraps the shared `Button` rather than restyling from scratch, and trims its
 * density so it fits inside a 62px bar — the same approach the hero's
 * `PrimaryCta` takes, so the two read as the same control.
 *
 * It renders a real <button> now, not a link. Opening a dialog is an action,
 * not navigation: there is no URL to link to, no href to middle-click, and a
 * link that never changes the URL is a control wearing the wrong costume.
 */
export const QueryCta = styled(Button)`
  font-size: 0.875rem;
  padding: 0.45rem 1.1rem;
  line-height: 1.2;
  white-space: nowrap;
  font-weight: 600;
  color: ${({ theme }) => theme.background};
  background: ${({ theme }) => theme.accent};
  border-color: ${({ theme }) => theme.accent};

  /* Beats the global button:hover rule in globalStyles.ts, which lifts and
     shadows every button and would make the navbar jump. The shadow is dropped
     too: Button.tsx adds one on hover, which reads as correct on a page but
     wrong inside a flat sticky bar. */
  &&:hover {
    background: ${({ theme }) => theme.text};
    border-color: ${({ theme }) => theme.text};
    color: ${({ theme }) => theme.background};
    transform: none;
    box-shadow: none;
  }

  &&:active {
    transform: none;
    box-shadow: none;
  }

  ${focusRing}
  ${noMotion}
`;

/* ----------------------------------------------------------- menu button */

/** Only rendered below DESKTOP_QUERY, so it needs no media query of its own. */
export const MenuButton = styled.button`
  position: relative;
  /* 44px touch target, per the mobile menu requirements. */
  width: 44px;
  height: 44px;
  margin-right: -10px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: none;
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.text};
  cursor: pointer;

  /* globalStyles.ts lifts and shadows every button on hover, which is wrong for
     a chrome control. See contract 2. */
  &:hover {
    transform: none;
    box-shadow: none;
    color: ${({ theme }) => theme.headingColor};
  }

  &:active {
    transform: none;
    box-shadow: none;
  }

  ${focusRing}
  ${noMotion}
`;

/* Two bars that rotate into an X. Drawn in CSS rather than pulled from an icon
   set: it is the only icon the navbar needs, and this keeps the open/close
   transition continuous instead of swapping two glyphs. */
export const MenuIcon = styled.span<{ $open: boolean }>`
  position: relative;
  display: block;
  width: 20px;
  height: 2px;
  background: currentColor;
  border-radius: 2px;
  transition: background-color 0.2s ease;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    height: 100%;
    background: currentColor;
    border-radius: 2px;
    transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), top 0.2s ease;
  }

  &::before {
    top: ${({ $open }) => ($open ? '0px' : '-6px')};
    transform: ${({ $open }) => ($open ? 'rotate(45deg)' : 'none')};
  }

  &::after {
    top: ${({ $open }) => ($open ? '0px' : '6px')};
    transform: ${({ $open }) => ($open ? 'rotate(-45deg)' : 'none')};
  }

  ${({ $open }) => ($open ? 'background-color: transparent;' : '')};

  @media (prefers-reduced-motion: reduce) {
    &,
    &::before,
    &::after {
      transition: none;
    }
  }
`;

/* ------------------------------------------------------- mobile menu open */

/** Clicks outside the panel close it, and it stops the page behind scrolling. */
export const Backdrop = styled(motion.div)`
  position: fixed;
  inset: ${NAVBAR_HEIGHT} 0 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: -1;
`;

export const MobilePanel = styled(motion.div)`
  overflow: hidden;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  background: ${({ theme }) => withAlpha(theme.background, 0.98)};
`;

export const MobileInner = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0.5rem 0 1.25rem;
`;

export const MobileList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

export const MobileItem = styled.li`
  list-style: none;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};

  &:last-of-type {
    border-bottom: 0;
  }
`;

export const MobileLink = styled(Link)<NavLinkProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 52px;
  padding: 0 0.25rem;
  font-size: 1rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  text-decoration: none;
  color: ${({ $active, theme }) => ($active ? theme.headingColor : theme.text)};
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.accent};
    text-decoration: none;
  }

  ${focusRing}
  ${noMotion}
`;

/** The active route is marked by a dot rather than an underline here, because a
 *  full-width underline in a stacked list reads as a border. */
export const MobileMarker = styled.span<{ $active: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $active, theme }) => ($active ? theme.accent : 'transparent')};
`;

/** The mobile-panel twin of NavAction: a full-width row that opens a window. */
export const MobileAction = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 52px;
  padding: 0 0.25rem;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.2;
  text-align: left;
  color: ${({ theme }) => theme.text};
  background: none;
  border: 0;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.accent};
    /* Same global button:hover reset as NavAction; see the note there. */
    transform: none;
    box-shadow: none;
  }

  ${focusRing}
  ${noMotion}
`;

/* The Query CTA inside the menu: the same object as the desktop button, but
   full width and sitting apart from the navigation list. */
export const MobileCta = styled(QueryCta)`
  width: 100%;
  min-height: 46px;
  margin-top: 1rem;
`;
